import intlTelInput, { type Iti } from 'intl-tel-input';

/**
 * Country-code phone widget for the Blade forms that carry an input with id="phone"
 * (contact create and edit, company settings). The visible input loses its name; the
 * widget posts the full E.164 number as `phone_full` and the flag as `country_code`.
 *
 * BRO-1445: the widget and the server must agree on what a valid number is. The
 * server checks the number against libphonenumber's per-country rules for ANY number
 * type. intl-tel-input's `isValidNumber()` only checks the length, and only against
 * mobile ranges, so a UK-shaped number under an Argentine flag sailed through here and
 * was rejected on the server as "could not be parsed". `isValidNumberPrecise()` with
 * `validationNumberTypes: null` is the same check the server makes.
 */

const MESSAGES: Record<number, string> = {
    [-99]: 'Phone number could not be checked. Try again in a moment.',
    0: 'Not a valid number for the country shown. Check the flag and the digits.',
    1: 'Invalid country code. Choose the country from the flag list.',
    2: 'Phone number is too short.',
    3: 'Phone number is too long.',
    4: 'Add the area code.',
    5: 'Wrong number of digits for the country shown.',
};

function errorMessage(iti: Iti): string {
    const code = iti.getValidationError();
    if (code !== 0 && MESSAGES[code]) {
        return MESSAGES[code];
    }
    // getValidationError() answers 0 for a number that has the right length but is
    // not a real number for that country (the BRO-1445 case), so name the country.
    const country = iti.getSelectedCountryData().name;
    return country
        ? `Not a valid number for ${country}. Check the flag and the digits.`
        : MESSAGES[0];
}

document.addEventListener('DOMContentLoaded', () => {
    const input = document.querySelector<HTMLInputElement>('#phone');
    if (!input) return;

    // Only phone_full is submitted; the visible input holds the national part.
    input.removeAttribute('name');

    const iti = intlTelInput(input, {
        loadUtils: () => import('intl-tel-input/utils'),
        // Used when the input is empty. An input holding a +-prefixed number
        // sets the flag from the number instead, which is how the edit page
        // opens a non-UK contact under the right flag (BRO-1445).
        initialCountry: 'gb',
        countryOrder: ['gb', 'us'],
        separateDialCode: true,
        validationNumberTypes: null,
        hiddenInput: () => ({
            phone: 'phone_full',
            country: 'country_code',
        }),
    });

    const form = input.closest('form');
    if (!form) return;

    // Inline error under the widget, in place of the alert() this used to raise.
    const wrapper = input.closest('.iti') ?? input;
    const error = document.createElement('p');
    error.className = 'text-sm text-red-600 mt-1';
    error.id = 'phone-error';
    error.hidden = true;
    wrapper.insertAdjacentElement('afterend', error);

    const showError = (message: string) => {
        error.textContent = message;
        error.hidden = false;
        input.classList.add('border-red-500');
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', error.id);
    };

    const clearError = () => {
        error.hidden = true;
        input.classList.remove('border-red-500');
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
    };

    input.addEventListener('input', clearError);
    input.addEventListener('countrychange', clearError);

    // BRO-477: the library fills the hidden input on submit. Clear its internal
    // state first when the visible input is empty, so phone_full posts as empty.
    form.addEventListener('submit', (e) => {
        const nationalNumber = input.value.trim();

        console.debug('Phone submit - nationalNumber:', nationalNumber);

        if (nationalNumber === '') {
            console.debug('Phone is empty, calling setNumber to clear');
            iti.setNumber('');
            return;
        }

        // null means the utils have not loaded yet: let the server decide.
        if (iti.isValidNumberPrecise() === false) {
            e.preventDefault();
            showError(errorMessage(iti));
            input.focus();
        }
    });
});
